<template>
    <div class="idp-skill-pillar">
        <div :class="['pillar-header', pillarType]">
            <i :class="pillarIcon" aria-hidden="true"></i>
            <h4>{{ title }}</h4>
        </div>
        <div class="pillar-ratings">
            <div class="rating-item green">
                <div class="rating-header">
                    <span class="rating-dot"></span>
                    <span class="rating-label">Excelling</span>
                    <span v-if="trend('green')" :class="['trend-indicator', trend('green')]">
                        <i :class="trendIcon('green')"></i>
                    </span>
                </div>
                <p class="rating-value">{{ greenValue || "—" }}</p>
            </div>
            <div class="rating-item yellow">
                <div class="rating-header">
                    <span class="rating-dot"></span>
                    <span class="rating-label">Coping</span>
                    <span v-if="trend('yellow')" :class="['trend-indicator', trend('yellow')]">
                        <i :class="trendIcon('yellow')"></i>
                    </span>
                </div>
                <p class="rating-value">{{ yellowValue || "—" }}</p>
            </div>
            <div class="rating-item red">
                <div class="rating-header">
                    <span class="rating-dot"></span>
                    <span class="rating-label">Developing</span>
                    <span v-if="trend('red')" :class="['trend-indicator', trend('red')]">
                        <i :class="trendIcon('red')"></i>
                    </span>
                </div>
                <p class="rating-value">{{ redValue || "—" }}</p>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "IDPSkillPillar",
    props: {
        title: {
            type: String,
            required: true,
        },
        pillarType: {
            type: String,
            required: true,
            validator: (value) =>
                ["technical", "tactical", "physical", "mental"].includes(value),
        },
        greenValue: {
            type: String,
            default: "",
        },
        yellowValue: {
            type: String,
            default: "",
        },
        redValue: {
            type: String,
            default: "",
        },
        previousReport: {
            type: Object,
            default: null,
        },
    },
    computed: {
        pillarIcon() {
            const icons = {
                technical: "bi bi-gear-wide-connected",
                tactical: "bi bi-diagram-3",
                physical: "bi bi-heart-pulse",
                mental: "bi bi-lightbulb",
            };
            return icons[this.pillarType] || "bi bi-circle";
        },
    },
    methods: {
        trend(/* level */) {
            if (!this.previousReport) return null;
            // Compare with previous report to show trend
            // This would need actual comparison logic based on your data
            return null; // 'up', 'down', or 'same'
        },
        trendIcon(level) {
            const trendValue = this.trend(level);
            if (trendValue === "up") return "bi bi-arrow-up";
            if (trendValue === "down") return "bi bi-arrow-down";
            return "bi bi-dash";
        },
    },
};
</script>

<style scoped lang="scss">
.idp-skill-pillar {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
    transition: box-shadow 0.2s ease;

    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }
}

.pillar-header {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 1rem;
    color: #fff;
    font-weight: 600;

    i {
        font-size: 1.25rem;
    }

    h4 {
        margin: 0;
        font-size: 1rem;
        font-weight: 600;
    }

    &.technical {
        background: linear-gradient(135deg, #3b82f6, #1d4ed8);
    }

    &.tactical {
        background: linear-gradient(135deg, #8b5cf6, #6d28d9);
    }

    &.physical {
        background: linear-gradient(135deg, #f97316, #ea580c);
    }

    &.mental {
        background: linear-gradient(135deg, #14b8a6, #0d9488);
    }
}

.pillar-ratings {
    padding: 1rem;
}

.rating-item {
    padding: 0.75rem;
    border-radius: 8px;
    margin-bottom: 0.5rem;

    &:last-child {
        margin-bottom: 0;
    }

    &.green {
        background: rgba(34, 197, 94, 0.1);
        border-left: 3px solid #22c55e;

        .rating-dot {
            background-color: #22c55e;
        }

        .rating-label {
            color: #16a34a;
        }
    }

    &.yellow {
        background: rgba(234, 179, 8, 0.1);
        border-left: 3px solid #eab308;

        .rating-dot {
            background-color: #eab308;
        }

        .rating-label {
            color: #ca8a04;
        }
    }

    &.red {
        background: rgba(239, 68, 68, 0.1);
        border-left: 3px solid #ef4444;

        .rating-dot {
            background-color: #ef4444;
        }

        .rating-label {
            color: #dc2626;
        }
    }
}

.rating-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.375rem;
}

.rating-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
}

.rating-label {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.025em;
}

.trend-indicator {
    margin-left: auto;
    font-size: 0.75rem;

    &.up {
        color: #22c55e;
    }

    &.down {
        color: #ef4444;
    }

    &.same {
        color: #94a3b8;
    }
}

.rating-value {
    margin: 0;
    font-size: 0.9375rem;
    color: #334155;
    line-height: 1.5;
}

/* Mobile Responsive */
@media (max-width: 768px) {
    .pillar-header {
        padding: 0.875rem;

        i {
            font-size: 1.125rem;
        }

        h4 {
            font-size: 0.9375rem;
        }
    }

    .pillar-ratings {
        padding: 0.875rem;
    }

    .rating-item {
        padding: 0.625rem;
    }

    .rating-value {
        font-size: 0.875rem;
    }
}

@media (max-width: 480px) {
    .pillar-header {
        padding: 0.75rem;
    }

    .pillar-ratings {
        padding: 0.75rem;
    }

    .rating-item {
        padding: 0.5rem;
    }
}
</style>
